import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SettingsIcon from '@mui/icons-material/Settings';
import BarChartIcon from '@mui/icons-material/BarChart';
import PsychologyIcon from '@mui/icons-material/Psychology';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import SyncIcon from '@mui/icons-material/Sync';
import ScienceIcon from '@mui/icons-material/Science';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CalculateIcon from '@mui/icons-material/Calculate';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HistoryIcon from '@mui/icons-material/History';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import WorkspaceService from '../services/WorkspaceService';
import { viewTabTitle } from './workspaces/workspaceUtils';

// Kept in sync by hand with AtomApp.js's own WORKSPACE_ICON_MAP and the old
// WorkspaceMenuOverlay's copy (not imported directly, to avoid a circular
// import) -- a workspace whose icon isn't in this list just falls back to
// ViewModuleIcon.
const WORKSPACE_ICON_MAP = {
  MoveToInbox: MoveToInboxIcon,
  Inventory2: Inventory2Icon,
  LocalShipping: LocalShippingIcon,
  Settings: SettingsIcon,
  BarChart: BarChartIcon,
  Psychology: PsychologyIcon,
  Warehouse: WarehouseIcon,
  FactCheck: FactCheckIcon,
  Sync: SyncIcon,
  Science: ScienceIcon,
  Schedule: ScheduleIcon,
  Calculate: CalculateIcon,
  ViewModule: ViewModuleIcon,
  Handshake: HandshakeIcon,
  AdminPanelSettings: AdminPanelSettingsIcon,
  ManageAccounts: ManageAccountsIcon,
  AccountTree: AccountTreeIcon,
  History: HistoryIcon,
  Lightbulb: LightbulbIcon,
};

const PINNED_KEY = '__pinned__';
const MANAGE_KEY = '__manage__';
const NAV_BAR_HEIGHT = 40;

const pinId = (ws) => `${ws.workspaceType}::${ws.viewId || ''}`;

/**
 * One row inside a domain group's drag-to-reorder list (edit mode only --
 * see the per-group settings icon in the Pinned panel). Drag handle
 * replaces the up/down arrows while editing; same visual pattern as
 * ColumnConfigDialog.js's SortableColumnRow. Reordering is scoped to THIS
 * group only (see handleGroupDragEnd) -- per user direction, dragging
 * reorders "within the grouping", not across domains.
 */
const SortablePinnedRow = ({ ws, renderIcon, colors }) => {
  const id = pinId(ws);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };
  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.5, borderRadius: 1,
        bgcolor: isDragging ? 'rgba(34,197,94,0.08)' : 'transparent',
        '&:hover': { bgcolor: colors.hoverBg },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab', display: 'flex', alignItems: 'center', color: colors.textMuted,
          '&:hover': { color: '#22c55e' }, '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: 18 }} />
      </Box>
      {renderIcon(ws.icon)}
      <Typography variant="body2" sx={{ color: colors.textPrimary, flex: 1 }}>
        {ws.customLabel || ws.title}
      </Typography>
    </Box>
  );
};

/**
 * Persistent horizontal workspace navigation bar -- replaces the old
 * center-top "Menu" chip + full-screen WorkspaceMenuOverlay Dialog for
 * desktop layouts (that Dialog is kept as-is for the mobile layout, where a
 * permanent top bar doesn't fit). See project_workspace_nav_bar_redesign.
 *
 * Top-level buttons: "Pinned" (personal shortcuts) + one per WORKSPACE_GROUP
 * domain (Operations / Setup / Analytics & Simulation / Administration /
 * ...), click-to-toggle (not hover -- hover menus are unreliable on touch
 * devices and prone to mis-triggering). Clicking an open button, clicking a
 * workspace/tab, pressing Escape, or clicking outside the bar+panel all
 * close it. The expand panel overlays the content below (absolute
 * position) rather than pushing the layout down, and spans the full app
 * width rather than a narrow fixed-size popup -- per explicit user
 * direction. A search box at the right end searches across every domain
 * (and pinned items) at once, overriding whichever category button is
 * selected while a query is present.
 */
function WorkspaceNavBar({
  colors,
  pinnedWorkspaces,
  workspaceGroups,
  onOpenWorkspace,
  onPinChanged,
}) {
  const [openKey, setOpenKey] = useState(null); // PINNED_KEY | domain code | null
  const [search, setSearch] = useState('');
  const [availableWorkspaces, setAvailableWorkspaces] = useState([]);
  const [availableLoading, setAvailableLoading] = useState(false);
  const [availableLoaded, setAvailableLoaded] = useState(false);
  // Inverted on purpose (2026-10-01, per explicit user direction + screenshot): a
  // workspace tile's own sub-views now show EXPANDED BY DEFAULT -- there's enough
  // horizontal room laid out side-by-side (see the "Groups laid out as horizontal
  // columns" comment below) to show everything at once instead of making the user
  // click a chevron per workspace just to see what's inside it. This tracks which
  // ones the user has explicitly collapsed, not which one is expanded -- starts
  // empty (everything expanded) and only grows when a user actively collapses a
  // tile, rather than the old single-value "only one expanded at a time" state.
  const [collapsedWorkspaceCodes, setCollapsedWorkspaceCodes] = useState(() => new Set());
  const [busyKey, setBusyKey] = useState(null);
  // The RF app (FH-280): its own site, opened in a new tab from Warehouse Operations.
  const [rfAppUrl, setRfAppUrl] = useState('');
  useEffect(() => { WorkspaceService.getRfAppUrl().then(setRfAppUrl); }, []);
  // Which domain groups (within the Pinned panel) are currently in
  // drag-to-reorder edit mode -- toggled per group via its own settings
  // icon, not one global switch, per user direction ("add the settings icon
  // to each grouping"). While any group is being edited the panel is kept
  // open (see the click-outside/Escape effect below) so a drag isn't
  // interrupted mid-gesture.
  const [editGroups, setEditGroups] = useState(new Set());
  // Which domain groups' "+" (add more from this category) inline browse
  // list is expanded -- separate from editGroups, since browsing/pinning
  // and reordering are two different things you can do to a group.
  const [browseGroups, setBrowseGroups] = useState(new Set());
  // Optimistic order override, set immediately on drag-drop so the new
  // order shows up right away (both in the drag list itself and in the
  // plain arrow view once you toggle out of edit mode) instead of waiting
  // on the reorder API + refetch round trip. Cleared once that round trip
  // resolves and the real pinnedWorkspaces prop should have caught up. Real
  // bug found live 2026-09-28: without this, a drag looked like it "did
  // nothing" because nothing re-rendered until the parent's refresh landed.
  const [pinnedOverride, setPinnedOverride] = useState(null);
  const effectivePinned = pinnedOverride || pinnedWorkspaces || [];
  const containerRef = useRef(null);
  const dragSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Drop the optimistic override once the real prop reflects it (matching
  // length is a cheap-enough signal here -- reorders never change which
  // items are pinned, only their order, so a length match after a refresh
  // is a reliable "the real data has caught up" signal).
  useEffect(() => {
    if (pinnedOverride && (pinnedWorkspaces || []).length === pinnedOverride.length) {
      setPinnedOverride(null);
    }
  }, [pinnedWorkspaces, pinnedOverride]);

  // Loaded eagerly on mount, not lazily on first click -- the bar itself
  // needs to know WHICH category buttons to even show before any
  // interaction happens (see visibleGroups below): the full 6-group
  // WORKSPACE_GROUP list isn't role-filtered, only the workspaces inside
  // each group are (via this same /available call, server-side). Without
  // this, every user saw all 6 buttons -- including Administration/
  // Platform/Testing -- regardless of role, and clicking one they had no
  // access to just opened an empty panel. Real gap flagged by user
  // 2026-09-28: "why would [an inventory control person] need platform or
  // administration". See project_workspace_nav_bar_role_filtering.
  const ensureAvailableLoaded = useCallback(() => {
    if (availableLoaded || availableLoading) return;
    setAvailableLoading(true);
    WorkspaceService.getAvailableWorkspaces()
      .then((workspaces) => setAvailableWorkspaces(workspaces || []))
      .catch((err) => console.error('Failed to fetch available workspaces:', err))
      .finally(() => {
        setAvailableLoading(false);
        setAvailableLoaded(true);
      });
  }, [availableLoaded, availableLoading]);

  useEffect(() => {
    ensureAvailableLoaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Click-outside-to-close + Escape-to-close, standard mega-menu behavior --
  // suppressed while any group is in drag-to-reorder edit mode, so the
  // panel "stays open" and a drag gesture (or a stray click while dragging)
  // can't accidentally dismiss it mid-edit. Exiting edit mode (toggling the
  // group's settings icon back off) re-enables normal close behavior.
  useEffect(() => {
    if (!openKey || editGroups.size > 0) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpenKey(null);
      }
    };
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpenKey(null);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openKey, editGroups]);

  // Exiting the Pinned panel entirely (switching to another bar button, or
  // closing) always resets edit/browse mode -- don't let a forgotten
  // "still editing" state linger and keep suppressing close next time.
  useEffect(() => {
    if (openKey !== PINNED_KEY) {
      if (editGroups.size > 0) setEditGroups(new Set());
      if (browseGroups.size > 0) setBrowseGroups(new Set());
    }
  }, [openKey, editGroups, browseGroups]);

  const toggleGroupEdit = useCallback((domain) => {
    setEditGroups((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  }, []);

  const toggleGroupBrowse = useCallback((domain) => {
    ensureAvailableLoaded();
    setBrowseGroups((prev) => {
      const next = new Set(prev);
      if (next.has(domain)) next.delete(domain);
      else next.add(domain);
      return next;
    });
  }, [ensureAvailableLoaded]);

  // Drag-reorder within one domain group only -- rebuilds the full flat
  // pinnedWorkspaces order (what the reorder API actually expects) by
  // reshuffling just this domain's items among themselves and leaving every
  // other domain's items in their existing absolute positions. Per user
  // direction: dragging reorders "within the grouping", not across domains.
  const handleGroupDragEnd = useCallback((domain, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const domainItems = effectivePinned.filter((p) => (p.navGroup || 'OTHER') === domain);
    const ids = domainItems.map(pinId);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx === -1 || newIdx === -1) return;
    const reordered = arrayMove(domainItems, oldIdx, newIdx);
    let i = 0;
    const fullOrder = effectivePinned.map((p) =>
      (p.navGroup || 'OTHER') === domain ? reordered[i++] : p
    );
    setPinnedOverride(fullOrder); // paint the new order immediately
    WorkspaceService.reorderWorkspaces(
      fullOrder.map((p) => ({ workspaceType: p.workspaceType, viewId: p.viewId || null }))
    )
      .then(onPinChanged)
      .catch((err) => {
        console.error('Failed to reorder pins:', err);
        setPinnedOverride(null); // revert the optimistic paint -- the write didn't actually take
      });
  }, [effectivePinned, onPinChanged]);

  const groupMap = useMemo(() => {
    const map = {};
    (workspaceGroups || []).forEach((g) => { map[g.code] = g; });
    return map;
  }, [workspaceGroups]);

  const sortedGroups = useMemo(() => {
    return [...(workspaceGroups || [])].sort((a, b) => (a.sortOrder ?? 99) - (b.sortOrder ?? 99));
  }, [workspaceGroups]);

  // Only show a category button when this user actually has at least one
  // accessible workspace in it. Before availableWorkspaces has loaded, show
  // nothing rather than the full unfiltered list, so there's no flash of
  // buttons the user doesn't have access to.
  const visibleGroups = useMemo(() => {
    if (!availableLoaded) return [];
    const domainsWithAccess = new Set(availableWorkspaces.map((ws) => ws.navGroup || 'OTHER'));
    return sortedGroups.filter((g) => domainsWithAccess.has(g.code));
  }, [sortedGroups, availableWorkspaces, availableLoaded]);

  const isPinned = useCallback((workspaceType, viewId) => {
    return effectivePinned.some(
      (p) => p.workspaceType === workspaceType && (p.viewId || null) === (viewId || null)
    );
  }, [effectivePinned]);

  const key = (workspaceType, viewId) => `${workspaceType}::${viewId || ''}`;

  const handleTogglePin = useCallback(async (workspaceType, viewId) => {
    const k = key(workspaceType, viewId);
    setBusyKey(k);
    try {
      if (isPinned(workspaceType, viewId)) {
        await WorkspaceService.unpinWorkspace(workspaceType, viewId);
      } else {
        await WorkspaceService.pinWorkspace(workspaceType, null, viewId);
      }
      await onPinChanged();
    } catch (err) {
      console.error('Failed to toggle pin:', err);
    } finally {
      setBusyKey(null);
    }
  }, [isPinned, onPinChanged]);

  const handleMovePin = useCallback(async (idx, direction) => {
    const list = [...effectivePinned];
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    [list[idx], list[swapIdx]] = [list[swapIdx], list[idx]];
    setPinnedOverride(list); // paint immediately, same reasoning as handleGroupDragEnd
    try {
      await WorkspaceService.reorderWorkspaces(
        list.map((p) => ({ workspaceType: p.workspaceType, viewId: p.viewId || null }))
      );
      await onPinChanged();
    } catch (err) {
      console.error('Failed to reorder pins:', err);
      setPinnedOverride(null);
    }
  }, [effectivePinned, onPinChanged]);

  const handleSelect = useCallback((workspaceType, title, viewId) => {
    onOpenWorkspace(workspaceType, title, viewId ? { viewId } : null);
    setOpenKey(null);
    setSearch('');
  }, [onOpenWorkspace]);

  const handleBarButtonClick = useCallback((k) => {
    ensureAvailableLoaded();
    setSearch('');
    setOpenKey((prev) => (prev === k ? null : k));
  }, [ensureAvailableLoaded]);

  const handleSearchChange = useCallback((e) => {
    ensureAvailableLoaded();
    setSearch(e.target.value);
    if (e.target.value && !openKey) setOpenKey('__search__');
  }, [ensureAvailableLoaded, openKey]);

  const clearSearch = useCallback(() => {
    setSearch('');
    setOpenKey(null);
  }, []);

  // ---- Pinned grouped-by-domain list (identical grouping rule the old Dialog used) ----
  const pinnedGrouped = useMemo(() => {
    const grouped = {};
    effectivePinned.forEach((ws) => {
      const domain = ws.navGroup || 'OTHER';
      if (!grouped[domain]) grouped[domain] = [];
      grouped[domain].push(ws);
    });
    const sortedDomains = Object.keys(grouped).sort((a, b) => {
      const aOrder = groupMap[a]?.sortOrder ?? 99;
      const bOrder = groupMap[b]?.sortOrder ?? 99;
      return aOrder - bOrder;
    });
    return { grouped, sortedDomains };
  }, [effectivePinned, groupMap]);

  // ---- Search matches, spanning every domain + their tabs ----
  const searchMatches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return availableWorkspaces
      .map((ws) => {
        // Also match the workspace description, not just its title -- lets an
        // admin write a description that naturally mentions the terms people
        // actually search for (e.g. Inventory's description mentions "items"
        // and "stock" even though no tab is literally titled that) instead of
        // requiring an exact tab/title substring match. See
        // project_workspace_search_tab_extraction_fix.
        const titleMatches = ws.title?.toLowerCase().includes(q);
        const descriptionMatches = ws.description?.toLowerCase().includes(q);
        const matchingViews = (ws.views || []).filter((v) => v.label?.toLowerCase().includes(q));
        if (titleMatches || descriptionMatches) return ws;
        if (matchingViews.length > 0) return { ...ws, views: matchingViews };
        return null;
      })
      .filter(Boolean);
  }, [availableWorkspaces, search]);

  // ---- Workspaces belonging to the currently-open domain category ----
  const domainWorkspaces = useMemo(() => {
    if (!openKey || openKey === PINNED_KEY || openKey === MANAGE_KEY || openKey === '__search__') return [];
    return availableWorkspaces.filter((ws) => (ws.navGroup || 'OTHER') === openKey);
  }, [availableWorkspaces, openKey]);

  // ---- "Manage my menu": every workspace this user is authorized for,
  // across every domain, grouped the same way Pinned is -- this is the
  // explicit "go configure what's in your menu" surface the old Dialog's
  // "All Workspaces / Manage Pins" tab provided, reachable via the gear
  // icon at the right of the bar rather than buried inside Pinned. See
  // project_workspace_nav_bar_manage_menu.
  const manageGrouped = useMemo(() => {
    const grouped = {};
    availableWorkspaces.forEach((ws) => {
      const domain = ws.navGroup || 'OTHER';
      if (!grouped[domain]) grouped[domain] = [];
      grouped[domain].push(ws);
    });
    const sortedDomains = Object.keys(grouped).sort((a, b) => {
      const aOrder = groupMap[a]?.sortOrder ?? 99;
      const bOrder = groupMap[b]?.sortOrder ?? 99;
      return aOrder - bOrder;
    });
    return { grouped, sortedDomains };
  }, [availableWorkspaces, groupMap]);

  const renderIcon = (iconName, size = 18) => {
    const IconComp = WORKSPACE_ICON_MAP[iconName] || ViewModuleIcon;
    return <IconComp sx={{ fontSize: size, color: '#8b5cf6', flexShrink: 0 }} />;
  };

  // A workspace "tile" used in category grids, search results, and the
  // "Customize my menu" (settings) panel -- click the title to open it,
  // click the chevron to reveal its tabs inline (capped at this one extra
  // level, per mega-menu depth guidance). Pin/unpin toggles only render
  // when showPinControls is true -- everywhere except the settings panel is
  // pure navigation now, per explicit user direction: "only show the pin or
  // unpin icons in the settings mode."
  const renderWorkspaceTile = (ws, showPinControls = false) => {
    const hasViews = (ws.views || []).length > 0;
    const wsExpanded = !collapsedWorkspaceCodes.has(ws.workspaceCode);
    const wsPinned = isPinned(ws.workspaceCode, null);
    return (
      <Box key={ws.workspaceCode} sx={{ minWidth: 220 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.5, borderRadius: 1, '&:hover': { bgcolor: colors.hoverBg } }}>
          {renderIcon(ws.icon)}
          <Typography
            variant="body2"
            onClick={() => handleSelect(ws.workspaceCode, ws.title, null)}
            sx={{ color: colors.textPrimary, flex: 1, cursor: 'pointer', fontWeight: 500, '&:hover': { color: '#8b5cf6' } }}
          >
            {ws.title}
          </Typography>
          {showPinControls && (
            <Tooltip title={wsPinned ? 'Unpin' : 'Pin workspace'}>
              <IconButton
                size="small"
                disabled={busyKey === key(ws.workspaceCode, null)}
                onClick={() => handleTogglePin(ws.workspaceCode, null)}
                sx={{ color: wsPinned ? '#22c55e' : colors.textMuted }}
              >
                {busyKey === key(ws.workspaceCode, null) ? <CircularProgress size={14} /> :
                  wsPinned ? <PushPinIcon sx={{ fontSize: 15 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
          )}
          {hasViews && (
            <IconButton
              size="small"
              sx={{ color: colors.textMuted }}
              onClick={() => setCollapsedWorkspaceCodes((prev) => {
                const next = new Set(prev);
                if (wsExpanded) next.add(ws.workspaceCode); else next.delete(ws.workspaceCode);
                return next;
              })}
            >
              {wsExpanded ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
            </IconButton>
          )}
        </Box>
        {hasViews && wsExpanded && (
          <Box>
            {ws.views.map((v) => {
              const vPinned = isPinned(ws.workspaceCode, v.viewId);
              return (
                <Box
                  key={v.viewId}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, py: 0.5, pl: 4, pr: 0.5, borderRadius: 1,
                    cursor: 'pointer', '&:hover': { bgcolor: colors.hoverBg },
                  }}
                >
                  <Typography
                    variant="body2"
                    onClick={() => handleSelect(ws.workspaceCode, viewTabTitle(ws.title, v.label), v.viewId)}
                    sx={{ color: colors.textSecondary, flex: 1, fontSize: '0.82rem' }}
                  >
                    {v.label}
                  </Typography>
                  {showPinControls && (
                    <Tooltip title={vPinned ? 'Unpin this tab' : 'Pin this tab'}>
                      <IconButton
                        size="small"
                        disabled={busyKey === key(ws.workspaceCode, v.viewId)}
                        onClick={() => handleTogglePin(ws.workspaceCode, v.viewId)}
                        sx={{ color: vPinned ? '#22c55e' : colors.textMuted }}
                      >
                        {busyKey === key(ws.workspaceCode, v.viewId) ? <CircularProgress size={12} /> :
                          vPinned ? <PushPinIcon sx={{ fontSize: 13 }} /> : <PushPinOutlinedIcon sx={{ fontSize: 13 }} />}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  const isOpen = Boolean(openKey);

  return (
    <Box ref={containerRef} sx={{ position: 'relative', zIndex: 1150 }}>
      {/* Bar row */}
      <Box
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          height: NAV_BAR_HEIGHT, px: 2,
          bgcolor: colors.surface,
          borderBottom: `1px solid ${colors.border}`,
        }}
      >
        {/* Icon-only, no text label -- the pin icon already says what this is,
            and pairing it with the word "Pinned" read as confusing/redundant.
            Matches the header's own convention for FAST Studio/notifications/
            account (all icon-only with a hover tooltip, no label). */}
        <Tooltip title="Pinned workspaces">
          <IconButton
            onClick={() => handleBarButtonClick(PINNED_KEY)}
            size="small"
            sx={{
              color: openKey === PINNED_KEY ? '#22c55e' : colors.textSecondary,
              bgcolor: openKey === PINNED_KEY ? 'rgba(34, 197, 94, 0.12)' : 'transparent',
              borderRadius: 1,
              '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#22c55e' },
            }}
          >
            <PushPinIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Box sx={{ width: '1px', height: 20, bgcolor: colors.border, mx: 0.5 }} />

        {/* Real bug found live 2026-10-01: this used sortedGroups (every group, unfiltered)
            instead of visibleGroups (the same list the "Customize my menu" gear panel below
            already correctly uses, intersected with availableWorkspaces -- which the server
            already filters by both role AND plan tier). A STARTER tenant could see and click
            into every nav-group tab here, including ones with zero actual workspaces in
            them, even though the gear panel's own category columns were already correctly
            restricted to the 3 allowed groups. This top bar IS the tenant's real menu --
            PlanTierAccessService's whole point is that STARTER shouldn't be ABLE to select
            these groups, not just that they'd find them empty once they did. */}
        {visibleGroups.map((g) => (
          <Box
            key={g.code}
            onClick={() => handleBarButtonClick(g.code)}
            sx={{
              display: 'flex', alignItems: 'center', px: 1.5, py: 0.5, borderRadius: 1,
              cursor: 'pointer',
              color: openKey === g.code ? '#22c55e' : colors.textSecondary,
              bgcolor: openKey === g.code ? 'rgba(34, 197, 94, 0.12)' : 'transparent',
              fontWeight: 700, fontSize: '0.82rem',
              '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#22c55e' },
            }}
          >
            {g.label || g.code}
          </Box>
        ))}

        <Box sx={{ flexGrow: 1 }} />

        <TextField
          size="small"
          placeholder="Search workspaces..."
          value={search}
          onChange={handleSearchChange}
          onFocus={ensureAvailableLoaded}
          sx={{ width: 240, '& .MuiOutlinedInput-root': { height: 30, fontSize: '0.82rem' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: colors.textMuted }} />
              </InputAdornment>
            ),
            endAdornment: search && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={clearSearch}>
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Tooltip title="Customize my menu">
          <IconButton
            onClick={() => handleBarButtonClick(MANAGE_KEY)}
            size="small"
            sx={{
              color: openKey === MANAGE_KEY ? '#22c55e' : colors.textSecondary,
              bgcolor: openKey === MANAGE_KEY ? 'rgba(34, 197, 94, 0.12)' : 'transparent',
              '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.12)', color: '#22c55e' },
            }}
          >
            <SettingsIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Expand panel -- overlays the content below, full width, rather than
          reflowing the layout or being confined to a small fixed popup. */}
      {isOpen && (
        <Box
          sx={{
            position: 'absolute', top: '100%', left: 0, right: 0,
            maxHeight: '70vh', overflowY: 'auto',
            bgcolor: colors.background,
            borderBottom: `1px solid ${colors.border}`,
            boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
            p: 2,
          }}
        >
          {search ? (
            availableLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            ) : searchMatches.length === 0 ? (
              <Typography sx={{ color: colors.textMuted, textAlign: 'center', py: 4 }}>
                No workspaces or tabs match "{search}".
              </Typography>
            ) : (
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1.5 }}>
                {searchMatches.map((ws) => renderWorkspaceTile(ws, false))}
              </Box>
            )
          ) : openKey === PINNED_KEY ? (
            effectivePinned.length === 0 && browseGroups.size === 0 ? (
              <Typography sx={{ color: colors.textMuted, textAlign: 'center', py: 4 }}>
                Nothing pinned yet. Click the [+] next to a category below to add your first item, or use the settings icon at the right of this bar to browse everything at once.
              </Typography>
            ) : (
              // Groups laid out as horizontal columns (like the top category
              // bar itself), not stacked one below another -- per explicit
              // user direction 2026-10-01: "the layout of the pinned menus
              // ... like the gear selector on the right horizontally laid
              // out grouping." Each column scrolls its own contents
              // independently if it runs long; the row as a whole scrolls
              // horizontally if there are more groups than fit.
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'flex-start', overflowX: 'auto', pb: 1 }}>
              {visibleGroups.map((g) => {
                const domain = g.code;
                const isEditing = editGroups.has(domain);
                const isBrowsing = browseGroups.has(domain);
                const domainItems = pinnedGrouped.grouped[domain] || [];
                const addableItems = isBrowsing ? (manageGrouped.grouped[domain] || []) : [];
                return (
                  <Box key={domain} sx={{ width: 260, minWidth: 260, flexShrink: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                      <Typography
                        variant="overline"
                        sx={{ color: colors.textMuted, fontSize: '0.7rem', letterSpacing: '0.08em' }}
                      >
                        {groupMap[domain]?.label || domain}
                      </Typography>
                      {domainItems.length > 1 && (
                        <Tooltip title={isEditing ? 'Done reordering' : 'Reorder this group'}>
                          <IconButton
                            size="small"
                            onClick={() => toggleGroupEdit(domain)}
                            sx={{ color: isEditing ? '#22c55e' : colors.textMuted, p: 0.25 }}
                          >
                            <SettingsIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={isBrowsing ? 'Hide' : `Add more from ${groupMap[domain]?.label || domain}`}>
                        <IconButton
                          size="small"
                          onClick={() => toggleGroupBrowse(domain)}
                          sx={{ color: isBrowsing ? '#22c55e' : colors.textMuted, p: 0.25 }}
                        >
                          <AddIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {/* Reordering lives here (the live menu), not in Settings
                        (pure selection) -- see
                        project_workspace_nav_bar_settings_vs_pinned_split.
                        Default: up/down arrows. Click the group's own
                        settings icon to switch to drag-and-drop instead
                        (scoped to this group only) -- per user direction. */}
                    {isEditing ? (
                      <DndContext
                        sensors={dragSensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => handleGroupDragEnd(domain, event)}
                      >
                        <SortableContext items={domainItems.map(pinId)} strategy={verticalListSortingStrategy}>
                          <Box>
                            {domainItems.map((ws) => (
                              <SortablePinnedRow key={pinId(ws)} ws={ws} renderIcon={renderIcon} colors={colors} />
                            ))}
                          </Box>
                        </SortableContext>
                      </DndContext>
                    ) : domainItems.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {domainItems.map((ws) => {
                          const idx = effectivePinned.indexOf(ws);
                          // A whole-workspace pin's own tabs, shown inline and
                          // always expanded (no chevron/click to drill down) --
                          // per explicit user direction 2026-10-01: the pinned
                          // panel should give the full picture of a group at a
                          // glance. A pin of one specific view has no `views`
                          // (see WorkspaceController.getPinnedWorkspaces) and
                          // renders as a plain row, same as before.
                          const wsViews = ws.views || [];
                          return (
                            <Box key={pinId(ws)}>
                              <Box
                                sx={{
                                  display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.5, borderRadius: 1,
                                  cursor: 'pointer', '&:hover': { bgcolor: colors.hoverBg },
                                }}
                                onClick={() => handleSelect(ws.workspaceType, ws.customLabel || ws.title, ws.viewId)}
                              >
                                {renderIcon(ws.icon)}
                                <Typography variant="body2" sx={{ color: colors.textPrimary, flex: 1 }}>
                                  {ws.customLabel || ws.title}
                                </Typography>
                                <Box sx={{ display: 'flex' }} onClick={(e) => e.stopPropagation()}>
                                  <IconButton size="small" disabled={idx === 0} onClick={() => handleMovePin(idx, 'up')}>
                                    <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                  <IconButton size="small" disabled={idx === effectivePinned.length - 1} onClick={() => handleMovePin(idx, 'down')}>
                                    <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Box>
                              </Box>
                              {wsViews.length > 0 && (
                                <Box sx={{ pl: 3.5 }}>
                                  {wsViews.map((v) => (
                                    <Typography
                                      key={v.viewId}
                                      variant="body2"
                                      onClick={() => handleSelect(ws.workspaceType, viewTabTitle(ws.customLabel || ws.title, v.label), v.viewId)}
                                      sx={{
                                        color: colors.textSecondary, fontSize: '0.82rem', py: 0.4, px: 0.5,
                                        borderRadius: 1, cursor: 'pointer', '&:hover': { bgcolor: colors.hoverBg, color: '#8b5cf6' },
                                      }}
                                    >
                                      {v.label}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    ) : !isBrowsing ? (
                      <Typography variant="caption" sx={{ color: colors.textMuted, pl: 0.5 }}>
                        Nothing pinned here yet -- click [+] to add one.
                      </Typography>
                    ) : null}
                    {/* "+" expands this, inline, right below the group's
                        current pins -- every authorized workspace in this
                        category (pinned or not), so adding your first item
                        (or a new one) doesn't require leaving the Pinned
                        view for the global Settings panel. */}
                    {isBrowsing && (
                      <Box sx={{ mt: domainItems.length > 0 ? 1 : 0, pt: domainItems.length > 0 ? 1 : 0, borderTop: domainItems.length > 0 ? `1px dashed ${colors.border}` : 'none' }}>
                        {addableItems.length === 0 ? (
                          <Typography variant="caption" sx={{ color: colors.textMuted }}>
                            Nothing available in this category.
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {addableItems.map((ws) => renderWorkspaceTile(ws, true))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
              </Box>
            )
          ) : openKey === MANAGE_KEY ? (
            <>
              <Typography variant="body2" sx={{ color: colors.textMuted, mb: 1.5 }}>
                Everything you're authorized to use. Pin whatever you want in your menu — add or remove anytime.
              </Typography>
              {effectivePinned.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="overline"
                    sx={{ color: colors.textMuted, fontSize: '0.7rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}
                  >
                    Your Pinned Items
                  </Typography>
                  {/* Selection only here -- no reorder controls. Reordering
                      lives in the Pinned panel (the pin icon button), which
                      is the actual live menu; this is just "what's in it".
                      An explicit delete/trash icon reads more clearly here
                      than an ambiguous pin toggle for "remove from menu" --
                      see project_workspace_nav_bar_settings_vs_pinned_split. */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1 }}>
                    {effectivePinned.map((ws) => (
                      <Box
                        key={pinId(ws)}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.5, borderRadius: 1, '&:hover': { bgcolor: colors.hoverBg } }}
                      >
                        {renderIcon(ws.icon)}
                        <Typography variant="body2" sx={{ color: colors.textPrimary, flex: 1 }}>
                          {ws.customLabel || ws.title}
                        </Typography>
                        <Tooltip title="Remove from menu">
                          <IconButton
                            size="small"
                            disabled={busyKey === key(ws.workspaceType, ws.viewId)}
                            onClick={() => handleTogglePin(ws.workspaceType, ws.viewId)}
                            sx={{ color: colors.textMuted, '&:hover': { color: '#ef4444' } }}
                          >
                            {busyKey === key(ws.workspaceType, ws.viewId) ? <CircularProgress size={14} /> : <DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {availableLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                manageGrouped.sortedDomains.map((domain) => (
                  <Box key={domain} sx={{ mb: 2 }}>
                    <Typography
                      variant="overline"
                      sx={{ color: colors.textMuted, fontSize: '0.7rem', letterSpacing: '0.08em', display: 'block', mb: 0.5 }}
                    >
                      {groupMap[domain]?.label || domain}
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1.5 }}>
                      {manageGrouped.grouped[domain].map((ws) => renderWorkspaceTile(ws, true))}
                    </Box>
                  </Box>
                ))
              )}
            </>
          ) : availableLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : domainWorkspaces.length === 0 ? (
            <Typography sx={{ color: colors.textMuted, textAlign: 'center', py: 4 }}>
              No workspaces in this category yet.
            </Typography>
          ) : (
            // Horizontal row of columns, not a wrapping grid -- same layout convention
            // as the "Customize my menu" panel's own category columns below (see its
            // "like the gear selector" comment), per explicit user direction + a
            // reference screenshot: with sub-views now expanded by default (see
            // collapsedWorkspaceCodes above), there's enough width to lay every
            // workspace in this group out side-by-side with its views already visible,
            // rather than wrapping into a multi-row grid of collapsed tiles.
            <>
              <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3, alignItems: 'flex-start', overflowX: 'auto', pb: 1 }}>
                {domainWorkspaces.map((ws) => renderWorkspaceTile(ws, false))}
              </Box>
              {/* The RF app is its own site: a line under the columns, always in view (the row above scrolls sideways). */}
              {openKey === 'WAREHOUSE_OPS' && rfAppUrl ? (
                <Box sx={{ mt: 1, pt: 1, borderTop: `1px dashed ${colors.border}`, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    component="a"
                    href={rfAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setOpenKey(null)}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.5, borderRadius: 1, textDecoration: 'none', '&:hover': { bgcolor: colors.hoverBg } }}
                  >
                    <PhoneAndroidIcon sx={{ fontSize: 18, color: '#8b5cf6', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500, '&:hover': { color: '#8b5cf6' } }}>
                      RF App
                    </Typography>
                    <OpenInNewIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                  </Box>
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    The floor app for guns and phones. Opens in a new tab.
                  </Typography>
                </Box>
              ) : null}
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

export default WorkspaceNavBar;
